declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

const push = (payload: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    page_location: window.location.href,
    ...payload,
  });
};

interface BaseEvent {
  user_logged_in: boolean;
  user_id?: string;
}

interface GenerateQuoteEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  currency: string;
  value: number;
  quote_id: string;
  collection_postcode: string;
  delivery_postcode: string;
  distance_km?: number;
  estimated_delivery_minutes?: number;
  add_ons_available?: string[];
}

interface ViewServicesEvent extends BaseEvent {
  service_category?: string;
  services_displayed: string[];
  list_name: string;
}

interface SelectServiceEvent extends BaseEvent {
  service_type: string;
  service_name: string;
  currency: string;
  value: number;
  list_name: string;
  list_position?: number;
}

interface SelectTimeSlotEvent extends BaseEvent {
  service_type: string;
  quote_id: string;
  slot_type: "collection" | "delivery";
  slot_date: string;
  slot_time_start: string;
  slot_time_end: string;
  slot_label?: string;
  currency: string;
  value: number;
}

interface SelectVehicleEvent extends BaseEvent {
  service_type: string;
  quote_id: string;
  vehicle_type: string;
  vehicle_label: string;
  vehicle_capacity_kg?: number;
  currency: string;
  value: number;
  price_delta?: number;
}

interface SelectAddOnEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  add_on_id: string;
  add_on_name: string;
  add_on_price: number;
  currency: string;
  value: number;
  add_ons_selected: string[];
}

interface UsePromoCodeEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  promo_code: string;
  promo_type: "percentage" | "fixed" | "free";
  discount_value: number;
  currency: string;
  value: number;
  value_pre_discount: number;
}

interface Item {
  item_id: string;
  item_name: string;
  item_category: string;
  item_variant: string;
  price: number;
  quantity: number;
}

interface AddPaymentInfoEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  payment_type: string;
  coupon?: string;
  currency: string;
  value: number;
  items: Item[];
}

interface SignUpEvent {
  user_id: string;
  method: "email" | "google" | string;
  account_type: "personal" | "business";
  referral_source?: string;
  promo_code_at_signup?: string;
}

interface CreateAccountEvent {
  method: "email" | "google" | string;
  account_type: "personal" | "business";
  form_id: string;
}

interface SaveBookingEvent {
  user_id: string;
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  booking_id: string;
  currency: string;
  value: number;
  add_ons_selected?: string[];
  booking_step: string;
}

interface StartPaymentEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  coupon?: string;
  currency: string;
  value: number;
  items: Item[];
}

interface UpgradeOptionSelectedEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  upgrade_id: string;
  upgrade_name: string;
  upgrade_price: number;
  upgrade_category: string;
  currency: string;
  value: number;
  value_pre_upgrade: number;
}

interface AddInsuranceEvent extends BaseEvent {
  service_type: string;
  vehicle_type: string;
  quote_id: string;
  insurance_id: string;
  insurance_name: string;
  insurance_price: number;
  insurance_cover_value: number;
  currency: string;
  value: number;
}

interface FormErrorEvent {
  user_logged_in: boolean;
  form_id: string;
  form_name: string;
  error_field: string;
  error_type: "validation" | "server" | "promo";
  error_message: string;
  error_code?: string;
  service_type?: string;
  quote_id?: string;
}

export const track = {
  generateQuote: (params: GenerateQuoteEvent) =>
    push({ event: "generate_quote", ...params }),

  viewServices: (params: ViewServicesEvent) =>
    push({ event: "view_services", ...params }),

  selectService: (params: SelectServiceEvent) =>
    push({ event: "select_service", ...params }),

  selectTimeSlot: (params: SelectTimeSlotEvent) =>
    push({ event: "select_time_slot", ...params }),

  selectVehicle: (params: SelectVehicleEvent) =>
    push({ event: "select_vehicle", ...params }),

  selectAddOn: (params: SelectAddOnEvent) =>
    push({ event: "select_add_on", ...params }),

  usePromoCode: (params: UsePromoCodeEvent) =>
    push({ event: "use_promo_code", ...params }),

  addPaymentInfo: (params: AddPaymentInfoEvent) =>
    push({ event: "add_payment_info", ...params }),

  signUp: (params: SignUpEvent) =>
    push({ event: "sign_up", user_logged_in: true, ...params }),

  createAccount: (params: CreateAccountEvent) =>
    push({ event: "create_account", user_logged_in: false, ...params }),

  saveBooking: (params: SaveBookingEvent) =>
    push({ event: "save_booking", user_logged_in: true, ...params }),

  startPayment: (params: StartPaymentEvent) =>
    push({ event: "start_payment", ...params }),

  upgradeOptionSelected: (params: UpgradeOptionSelectedEvent) =>
    push({ event: "upgrade_option_selected", ...params }),

  addInsurance: (params: AddInsuranceEvent) =>
    push({ event: "add_insurance", ...params }),

  formError: (params: FormErrorEvent) =>
    push({ event: "form_error", ...params }),
};
